import { useState } from "react"
import { api } from "../../../../../env"
import { errMessage } from "../../../../../utils/errors/errorHandler"
import LoadingButton from "../../../../../components/LoadingButton"
import { composeDescription } from "../helpers"

function SummaryCell({ label, children }) {
    return (
        <div className="bt-summary-cell">
            <p className="bt-summary-label">{label}</p>
            <p className="card-body">{children}</p>
        </div>
    )
}

export default function Review({ blocks, dna, chapters, trackId, onEditStep, onDone }) {

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState("")

    const totalActivities = chapters.reduce((n, c) => n + (c.activities?.length ?? 0), 0)
    const totalSparks = chapters.reduce((n, c) => n + (c.sparks?.length ?? 0), 0)
    // Sparks whose transcript is still blank or just the "# title" placeholder, so publishing now
    // would ship them without a learning body.
    const missingTranscripts = chapters.reduce((n, c) => n + (c.sparks ?? []).filter(s => {
        const body = (s.content ?? "").trim()
        return body.length === 0 || (body.length < 40 && /^#\s/.test(body))
    }).length, 0)

    // The assembled chapters, in the BuildChapterDto shape publish expects.
    const chapterPayload = () => chapters.map(c => ({
        title: c.title,
        theme: c.theme,
        description: c.description,
        activities: (c.activities ?? []).map(a => ({ title: a.title, description: a.description })),
        sparks: (c.sparks ?? []).map(s => ({ name: s.name || s.displayName, displayName: s.displayName, description: s.description, content: s.content })),
    }))

    const publish = () => {
        setError("")
        setLoading(true)
        const payload = {
            name: blocks.name || "Generated Track",
            displayName: blocks.name || "Generated Track",
            description: composeDescription(blocks, dna),
            durationMonths: blocks.durationMonths ?? undefined,
            chapters: chapterPayload(),
        }
        // Finalize the in-progress draft in place. (Fallback to a fresh build if no draft exists.)
        const request = trackId ? api.post(`tracks/${trackId}/finalize`, payload) : api.post("tracks/build", payload)
        request
            .then(res => {
                setLoading(false)
                // Generated tracks stay Draft — open in the existing editor for final review/publish.
                onDone(res.data.id)
            })
            .catch(e => {
                setError(errMessage(e, "Couldn't publish the track. Please try again."))
                setLoading(false)
            })
    }

    return (
        <div className="card standard-padding flex columns gap-l showing">
            <div className="bt-publish-banner">
                <span className="material-symbols-outlined">auto_awesome</span>
                <p className="card-body"><b>{blocks.name || "This track"}</b> is ready to publish. {chapters.length} chapters, {totalActivities} Activities and {totalSparks} Sparks written.</p>
            </div>

            <div className="flex columns gap-s">
                <div className="flex space-between center gap-xs wrap">
                    <p className="card-title">Track summary</p>
                    <div className="flex gap-xs wrap">
                        <button className="text" onClick={() => onEditStep(0)}>Edit building blocks</button>
                        <button className="text" onClick={() => onEditStep(1)}>Edit Track DNA</button>
                    </div>
                </div>

                <div className="bt-summary-grid">
                    <SummaryCell label="Audience">{blocks.audience || "—"}</SummaryCell>
                    <SummaryCell label="Level">{blocks.level || "—"}</SummaryCell>
                    <SummaryCell label="Duration">{blocks.durationMonths ? `${blocks.durationMonths} months` : "—"}</SummaryCell>
                    <SummaryCell label="Identity shift">{(dna.currentIdentity || dna.futureIdentity) ? `${dna.currentIdentity || "?"} → ${dna.futureIdentity || "?"}` : "—"}</SummaryCell>
                    <SummaryCell label="Capabilities">{(dna.capabilities ?? []).filter(c => c.name).map(c => c.name).join(", ") || "—"}</SummaryCell>
                    <SummaryCell label="Core belief">{dna.coreBelief || "—"}</SummaryCell>
                </div>
            </div>

            <div className="flex columns gap-s">
                <p className="card-title">Chapters</p>
                {chapters.map((c, idx) => {
                    const nA = c.activities?.length ?? 0
                    const nS = c.sparks?.length ?? 0
                    return (
                        <div key={idx} className="bt-review-chapter flex space-between center gap-xs">
                            <div className="flex center gap-xs">
                                <span className="bt-step-num">{idx + 1}</span>
                                <div className="flex columns gap-xxs">
                                    <p className="card-body medium">{c.title || "Untitled chapter"}</p>
                                    <p className="card-label neutral">{(nA === 0 && nS === 0) ? "No Activities or Sparks written yet." : `${nA} Activities · ${nS} Sparks`}</p>
                                </div>
                            </div>
                            <button className="text" onClick={() => onEditStep(2)}>Edit</button>
                        </div>
                    )
                })}
            </div>

            {missingTranscripts > 0 && (
                <div className="bt-publish-banner" style={{ background: "rgba(179, 38, 30, 0.08)" }}>
                    <span className="material-symbols-outlined">warning</span>
                    <p className="card-body">{missingTranscripts} {missingTranscripts === 1 ? "spark has" : "sparks have"} no full transcript yet. Use "Generate" or "Write full transcript with AI" on the Chapters step, or publish as is and add them later.</p>
                </div>
            )}

            {error && <p className="card-label error showing">{error}</p>}

            <div className="flex space-between center gap-xs bt-footer bt-footer-merged">
                <button className="outline" onClick={() => onEditStep(2)}>Edit chapters</button>
                <LoadingButton className="solid" label="Publish track" leadingIcon="check" loading={loading} onClick={publish} />
            </div>
        </div>
    )
}
